// 訓練期程查詢整頁（training-plan-inst-plan-inst-list＝legacy training/planInst/planInstList.jsp
// ＋planInstFormSearchPlanInst.jsp＋planInstList_js.jsp）。批次 49 Wave 2（b49-planinst）：
// complexBody 整頁置換（批次 41 ClassFormListBody／同模組 ArrangeTrainBody 先例）。
//
// ── 為什麼要 complexBody（規格 .tmp/tasks/orca-tasks/b49-planinst-spec.md §0/§3；Lead 十點裁定）──
// 泛用 CrudListPage＋PLAN_INST_BEHAVIOR 已能渲染查詢 form＋主表＋明細（批次 15 B7-1），但
// 103 筆結構差異的主要來源是「泛用模板表達不了的 legacy 形狀」：
//   - label-per-group：legacy 一個 label 對（input＋select）兩個控制項（學員/導師/臨床教師群，
//     planInstFormSearchPlanInst.jsp:152-192），LegacyContractFields 是 label-per-field →
//     12 個 label vs legacy 9 個＋「臨床教師 vs 導師」錯位（generated 紅線不可動）；
//   - searchClinicalNo input 無 name（:182-184）→ 抽出器整個丟掉；
//   - btnAdd 在 panel 層（form 外，planInstList.jsp:40）、importForm 是獨立 form（:49-81）、
//     departmentId 的 hidden form-group（:1386）、兩欄 layout；
//   - dynamic select 初始態：annualId=1「115」／organizationId=1「本機帳號」／jobTypeId=
//     請選擇＋1「系統管理」，其餘 6 個 0 options（OPTION_SET_OVERRIDES 整組取代假選項，
//     legacy-fields.ts，規格 §4.1/§4.4＋ruling-1）；
//   - 隱藏範本×4：planInstDetailSample（:1499-1523，兩列表頭）＋templateForFiller/
//     TeacherPool/Any（templateForFiller.jsp:2-20）。
//
// ── DOM 對齊（normalize-dom 語意，Wave 3 diff 的配對依據）──────────────────────
//   - 查詢 form 子序列照 legacy 正規化樹逐項：planId/action hidden→9 群 label＋控制項→
//     查詢鈕（button type=submit vs legacy input[type=button]＝GNL-004 同族形態差，裁決 3
//     不 hack）→csrf 不畫（Q2 裁決，缺元素 1 筆既定殘留）。
//   - importForm action=""（正規化成 /）＋子序列：label 匯入期程資料→uploadFile→期程匯入→
//     div@hidden（tsghImportRow，裁決 6 顯隱）→使用說明 label→範本下載→ul>li。
//   - hr-line-dashed 與包裝 div（grid/label-group 的 flex 容器）皆是 class-only 節點，
//     normalize R6 剝除＝兩側同形；隱藏範本的 wrapper 用 class="hidden"（非 hidden 屬性）：
//     legacy 的範本 wrapper 本身就是 class="hidden"（Bootstrap，normalize L6 盲區不認得）→
//     兩側同樣被剝除、內容浮到同層——hidden 屬性會讓 react 側多出 @hidden 包裝層（新
//     順序/層級差，§7-1 禁止），故此處刻意不照 §6-7 的字面建議（差異理由已向 Lead 報告）。
//   - select2 裝飾（jobTypeId/planTargetId）＝R4 vendor stub 兩側皆剝，不畫。
//
// ── 誠實標註（KNOWN-NOT-DONE／【雛形值待實機校準】）──────────────────────────
//   - 查詢 filters 直送 MockDataSource（matchesFilters 精確比對）；planId=0 常數 seed 一律
//     進 payload（C5-h 不可驅動帳）→ 查詢後 0 列＝伺服器端過濾語意的 mock 揭露
//     （ClassFormListBody §47-50 先例文案）。
//   - 級聯（annualId/organizationId→jobTypeId；jobTypeId→planTarget；planTarget→phaseTarget）
//     在 0/1 選項集下無素材，僅 jobTypeId→tsghImportRow 顯隱（裁決 6）照鏡像。
//   - 權限 gating（isStudent/isMentor/shiro）不實作（裁決 7，既有先例）；自動載入維持現狀
//     （裁決 5）。
//
// 【證據等級】§7 第 3 級（原始碼，行號親讀 2026-10-06）。

import { useRef, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, useDialogs } from '@/components/layouts';
import { DataTableShell } from '@/components/layouts/DataTableShell';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { t } from '@/messages/zh-TW';
import {
  columnsFor,
  DEFAULT_PAGE_SIZE,
  initialValues,
  legacyContractFieldsFor,
  legacyTableSequenceFor,
  resolveLegacyMessage,
  resolveLegacyMessageForAction,
  toPayload,
  useDataSource,
  useList,
  type LegacyFieldControl,
  type Row,
} from '@/mocks';
// optionSetFor 尚未納入 '@/mocks' 公開入口（index.ts 不在准動檔）——直接路徑 import
// （dynamic-select-prompt 同款先例，LegacyContractFields）。
import { optionSetFor } from '@/mocks/legacy-fields';
import { selectPromptFor } from '@/mocks/dynamic-select-prompt';
import { TRAINING_JOB_TYPE_OPTIONS } from '@/mocks/resource-rules';
import { formSpecFor } from '@/mocks/legacy-form-specs';
import {
  PlanInstDetailCell,
  PlanInstDetailRows,
  PlanInstFormsOpener,
  PlanInstImportToolbar,
} from '@/pages/training/PlanInstDetail';
import { PlanInstEditModal } from '@/pages/training/PlanInstEditModal';
import type { BehaviorFlowContext } from '../templates/page-behaviors';
import { rowUiKey, TemplateError, toUiKey } from '../templates/shared';

export function PlanInstListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 契約欄位（csrf 已由 legacyContractFieldsFor 排除；uploadFile 在 importForm 畫）。
  // 初始值＝initialValues（planId seed '0'）＋OPTION_SET_OVERRIDES 命中欄接 legacy
  // selected（W9 SELECT_DEFAULT 同款接線，規格 §4.1；override 首筆＝legacy 唯一真選項）
  // 【雛形值待實機校準】。
  // ------------------------------------------------------------------
  const contractFields = legacyContractFieldsFor(pageId) ?? [];
  const fieldOf = (name: string): LegacyFieldControl => {
    const field = contractFields.find((candidate) => candidate.name === name);
    if (field === undefined) {
      throw new Error(`training-plan-inst-plan-inst-list 契約欄位缺 ${name}`);
    }
    return field;
  };
  const formSpec = formSpecFor(pageId);
  const [draftValues, setDraftValues] = useState<Record<string, string>>(() => {
    const base = initialValues(contractFields, pageId);
    for (const field of contractFields) {
      const override = optionSetFor(pageId, field.name);
      if (override !== undefined && override.length > 0) {
        base[field.name] = override[0]?.value ?? '';
      }
    }
    return base;
  });
  /** searchClinicalNo 無 name（legacy :182-184 抽出器不記）→ 非契約欄位；刻意用**非受控**
   *  input＋ref（受控 value 會序列化 value="" 屬性差＝R8 家族第 3 筆；legacy 該 input 無
   *  value 屬性）。eraser 清除走 ref（legacy eraseClinicalId 的 $('#searchClinicalNo').val('')）。 */
  const clinicalNoRef = useRef<HTMLInputElement | null>(null);

  const setFieldValue = (name: string, next: string) => {
    setDraftValues((current) => ({ ...current, [name]: next }));
  };

  // ------------------------------------------------------------------
  // 清單（doQuery planInstList_js.jsp:142-166；查詢鈕→serialize→getPlanInstListAjax。
  // 雛形 filters 直送 MockDataSource；useList mount 即取資料＝裁決 5 維持現狀）。
  // ------------------------------------------------------------------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [keyword, setKeyword] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { data, loading, error, reload } = useList(pageId, { page, pageSize, keyword, filters });
  const rows = data?.rows ?? [];

  function applySearch() {
    // toPayload：不可驅動欄位（hidden、0-option select）一律帶 key（C5-h）；可驅動空的
    // 不帶。planId='0' 常數 seed 進 filters→matchesFilters 全等比對 0 列＝伺服器端過濾
    // 語意揭露【雛形值待實機校準】。
    setFilters(toPayload(draftValues, contractFields, pageId));
    setPage(1);
  }

  // ------------------------------------------------------------------
  // select 選項（OPTION_SET_OVERRIDES replace 語意；selectPromptFor 並存＝首項 prompt）。
  // 0 options（override 空陣列）＝連 prompt 都不畫（渲染端 flag，規格 §4.4/§8-2 裁決 2）。
  // ------------------------------------------------------------------
  function renderContractSelect(name: string): ReactElement {
    const field = fieldOf(name);
    const uiId = `${pageId}-search-${toUiKey(name)}`;
    const override = optionSetFor(pageId, name);
    const onChange = (next: string) => setFieldValue(name, next);
    if (override !== undefined && override.length === 0) {
      return (
        <Select
          key={name}
          uiId={uiId}
          name={field.name}
          required={field.required}
          value=""
          onChange={onChange}
          options={[]}
        />
      );
    }
    const prompt = selectPromptFor(pageId, name);
    return (
      <Select
        key={name}
        uiId={uiId}
        name={field.name}
        required={field.required}
        value={draftValues[name] ?? ''}
        onChange={onChange}
        options={(override ?? []).map((option) => ({ value: option.value, label: option.text }))}
        {...(prompt === undefined ? {} : { placeholder: prompt.text })}
      />
    );
  }

  /** label＋控制項群組（legacy form-group：normalize 後 label 與控制項同層；flex 容器
   *  class-only 會被 R6 剝除，兩側同形）。 */
  function labelGroup(label: string, children: ReactElement[]): ReactElement {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字複製 legacy 的純文字 label（控制項是兄弟節點；LegacyContractFields 同款）。 */}
        <label className="mb-[5px] inline-block font-bold text-ink">{label}</label>
        {children}
      </div>
    );
  }

  // ------------------------------------------------------------------
  // 編輯 modal（showModalPlanInst(0,'add')／學員連結 .showEdit :122-127→modify）。
  // ------------------------------------------------------------------
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; row: Row | null } | null>(null);
  const openEdit = (row: Row) => {
    setEditing({ mode: 'edit', row });
  };
  const openCreate = () => {
    setEditing({ mode: 'create', row: null });
  };

  // ------------------------------------------------------------------
  // 明細展開（details-control／row.child；狀態由 body 持有，渲染責任自
  // PLAN_INST_BEHAVIOR 搬進 body——complexBody 早退下泛用 hooks 是死碼）。
  // ------------------------------------------------------------------
  const [expandedRowIds, setExpandedRowIds] = useState<ReadonlySet<string>>(new Set());
  const toggleRowExpand = (rowId: string) => {
    setExpandedRowIds((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };
  const flowCtx: BehaviorFlowContext = {
    pageId,
    reload,
    openEdit,
    openCreate,
    dataSource,
    dialogs,
    expandedRowIds,
    toggleRowExpand,
  };

  // ------------------------------------------------------------------
  // 主表（planInstListTable；表頭序列 legacy-table-headers 7 欄，儲存格渲染自
  // PLAN_INST_BEHAVIOR.cellOverride 搬入）。
  // ------------------------------------------------------------------
  const columns = columnsFor(pageId);
  const sequence = legacyTableSequenceFor(pageId);
  const tableColumns: readonly TableColumn[] =
    sequence === undefined
      ? columns.map((column) => ({ key: column.key, header: column.label }))
      : sequence.map((column) => ({ key: column.key, header: column.header }));

  const legacyEmptyMessage = resolveLegacyMessage(pageId, 'empty');
  async function exportRows() {
    try {
      await dataSource.list(pageId, { page, pageSize, keyword, filters, export: true });
    } catch {
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', 'export')?.text ?? t('error.generic'),
      );
      return;
    }
    await dialogs.info(t('export.notice'));
  }

  const resultShell = (
    <DataTableShell
      uiId={`${pageId}-result`}
      page={page}
      pageSize={pageSize}
      total={data?.total ?? 0}
      keyword={keyword}
      loading={loading}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
      onKeywordChange={(next) => {
        setKeyword(next);
        setPage(1);
        reload();
      }}
      {...(legacyEmptyMessage === null ? {} : { emptyText: legacyEmptyMessage.text })}
      exportEffect={`list:${pageId}#export`}
      onExport={() => {
        void exportRows();
      }}
    >
      <Table
        uiId={`${pageId}-result-table`}
        columns={tableColumns}
        rows={rows}
        rowKey={rowUiKey}
        // 空表文案由 DataTableShell 負責，這裡給空字串避免同一畫面出現兩次（ClassFormListBody 同款）。
        emptyText=""
        expandedKeys={
          new Set(
            rows
              .filter((candidate) => expandedRowIds.has(String(candidate.id ?? '')))
              .map((candidate, mapIndex) => rowUiKey(candidate, mapIndex)),
          )
        }
        expandedRowRender={(row: Row) => (
          <PlanInstDetailRows
            pageId={pageId}
            row={row}
            uiKey={toUiKey(String(row.id ?? ''))}
            ctx={flowCtx}
          />
        )}
        renderCell={(row: Row, columnKey: string) => {
          const uiKey = toUiKey(String(row.id ?? ''));
          // 明細欄（details-control，planInstList_js.jsp:64-69/:128-136）。
          if (columnKey === 'text') {
            return <PlanInstDetailCell pageId={pageId} row={row} uiKey={uiKey} ctx={flowCtx} />;
          }
          // 空表頭欄＝「表單明細」鈕（showFormDetail('form', row.id, row.studentIdName) :70-78）。
          if (columnKey === 'actions') {
            return <PlanInstFormsOpener pageId={pageId} row={row} uiKey={uiKey} />;
          }
          if (columnKey === 'date') {
            // 學年度（annualIdName；1911 顯示差＝loginAccountAnnualDisp 未知，種子已是民國年
            // 【雛形值待實機校準】）。
            return <>{String(row['annualIdName'] ?? '')}</>;
          }
          if (columnKey === 'title') {
            // 訓練計畫（planTargetIdName＋isActive==0→`<font color='red'>(己停用)</font>` :91-100）。
            const text = String(row['planTargetIdName'] ?? '');
            return String(row['isActive'] ?? '') === '0' ? (
              <span>
                {text}
                <span className="text-red-600">(己停用)</span>
              </span>
            ) : (
              <>{text}</>
            );
          }
          if (columnKey === 'person') {
            // 學員＝`a.showEdit`→showModalPlanInst(row.id,'modify',row)（:101-110/:122-127）。
            return (
              // biome-ignore lint/a11y/useSemanticElements: 換成 <button> 會讓標籤名與原系統不同（原系統是 <a class="showEdit">），直接多一筆 structure 差異
              <a
                data-ui-id={`${pageId}-result-row-${uiKey}-student-link`}
                className={`${LINK_TEXT_CLASS} cursor-pointer`}
                role="button"
                tabIndex={0}
                // biome-ignore lint/a11y/useValidAnchor: 原系統的 <a> 沒有 href（onclick 驅動）；href 在 normalize-dom 屬性 allowlist 內，補上就是一筆差異。鍵盤等效操作已由 tabIndex + onKeyDown 提供。
                onClick={() => openEdit(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEdit(row);
                  }
                }}
              >
                {String(row['studentIdName'] ?? '')}
              </a>
            );
          }
          if (columnKey === 'category') {
            // 組別：legacy 依 jobType data-isgroup 整欄顯隱（:111-117/:174-186 showQuery）；
            // Table 無動態欄 hook，雛形以 cell 內容呈現（非 isGroup 組→空字串）
            // 【雛形值待實機校準】。
            const jobType = TRAINING_JOB_TYPE_OPTIONS.find(
              (option) => option.value === String(row['jobTypeId'] ?? ''),
            );
            return jobType?.isGroup === '1' ? <>{String(row['phaseTargetIdName'] ?? '')}</> : <>{''}</>;
          }
          if (columnKey === 'person2') {
            return <>{String(row['mentorIdName'] ?? '')}</>;
          }
          const raw = row[columnKey];
          return raw === undefined || raw === null ? '' : String(raw);
        }}
      />
    </DataTableShell>
  );

  // ------------------------------------------------------------------
  // 整頁（legacy 順序：新增鈕（panel 層）→兩欄 grid→hr→主表→detailSample→範本群）。
  // 隱藏範本在 JSX 上置於主表區之前／detailSample 之後——normalize 後與 legacy 的 root
  // 子序列（table、table、select×3、input、select）LCS 全配（見檔頭 DOM 對齊）。
  // ------------------------------------------------------------------
  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="訓練期程"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {error !== null ? (
          <TemplateError uiId={`${pageId}-error`} error={error} onRetry={reload} pageId={pageId} />
        ) : null}
        {/* btnAdd（planInstList.jsp:38-40）：ibox-content 層＝search form 外（panel 層）。
            data-modal-trigger 自報（D-3；editModalTriggerId 沿用批次 15 值）。 */}
        <div className="mb-3">
          <Button
            uiId={`${pageId}-toolbar-add`}
            variant="primary"
            size="sm"
            type="button"
            dataModalTrigger={`${pageId}-plan-inst-dialog`}
            onClick={openCreate}
          >
            新增
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {/* 查詢 form（searchPlanInstForm；action/method＝legacy-form-specs 白名單鏡像）。 */}
          <div>
            <form
              data-ui-id={`${pageId}-search`}
              {...(formSpec === undefined ? {} : { action: formSpec.action, method: formSpec.method })}
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                applySearch();
                reload();
              }}
            >
              <input
                type="hidden"
                name="planId"
                value={draftValues['planId'] ?? ''}
                onChange={(event) => setFieldValue('planId', event.target.value)}
              />
              <input
                type="hidden"
                name="action"
                value={draftValues['action'] ?? ''}
                onChange={(event) => setFieldValue('action', event.target.value)}
              />
              {labelGroup('* 學年度：', [
                renderContractSelect('annualId'),
              ])}
              {labelGroup('* 醫院：', [
                renderContractSelect('organizationId'),
              ])}
              {labelGroup('* 訓練類別：', [
                renderContractSelect('jobTypeId'),
              ])}
              {/* departmentId：legacy form-group hidden（raw-dom:1386，Bootstrap .hidden class
                  ＝normalize 不認得→兩側同被剝除）。class="hidden" 鏡像（見檔頭 DOM 對齊）。 */}
              <div className="hidden">
                {labelGroup('* 科部：', [
                  renderContractSelect('departmentId'),
                ])}
              </div>
              {labelGroup('* 訓練計畫：', [
                renderContractSelect('planTargetId'),
              ])}
              {labelGroup('組別：', [
                renderContractSelect('phaseTargetId'),
              ])}
              {labelGroup('學員：', [
                <TextInput
                  key="searchStudentCode"
                  uiId={`${pageId}-search-search-student-code`}
                  name="searchStudentCode"
                  value={draftValues['searchStudentCode'] ?? ''}
                  placeholder="(請輸入姓名或員工編號)"
                  onChange={(next) => setFieldValue('searchStudentCode', next)}
                />,
                renderContractSelect('studentId'),
              ])}
              {labelGroup('導師：', [
                <TextInput
                  key="searchMentorNo"
                  uiId={`${pageId}-search-search-mentor-no`}
                  name="searchMentorNo"
                  value={draftValues['searchMentorNo'] ?? ''}
                  placeholder="(請輸入姓名或員工編號)"
                  onChange={(next) => setFieldValue('searchMentorNo', next)}
                />,
                renderContractSelect('mentorId'),
                // mentorEraserBtn（planInstFormSearchPlanInst.jsp:175；eraseMentorId :619-622）。
                <button
                  key="mentorEraser"
                  type="button"
                  data-ui-id={`${pageId}-mentor-eraser`}
                  aria-label="清除導師"
                  className="inline-block size-8 rounded-full border border-border text-ink hover:bg-surface-subtle"
                  onClick={() => {
                    setFieldValue('searchMentorNo', '');
                    setFieldValue('mentorId', '');
                  }}
                />,
              ])}
              {labelGroup('臨床教師：', [
                <input
                  key="searchClinicalNo"
                  ref={clinicalNoRef}
                  type="text"
                  data-ui-id={`${pageId}-search-clinical-no`}
                  placeholder="(請輸入姓名或員工編號)"
                  className="block w-full rounded-[1px] border bg-surface px-3 py-[6px] text-[16px] text-ink placeholder:text-ink-muted transition-colors focus:border-brand"
                />,
                renderContractSelect('clinicalId'),
                // 臨床教師清除鈕（:190 無 id；eraseClinicalId :623-626：清 input＋select）。
                <button
                  key="clinicalEraser"
                  type="button"
                  data-ui-id={`${pageId}-clinical-eraser`}
                  aria-label="清除臨床教師"
                  className="inline-block size-8 rounded-full border border-border text-ink hover:bg-surface-subtle"
                  onClick={() => {
                    if (clinicalNoRef.current !== null) {
                      clinicalNoRef.current.value = '';
                    }
                    setFieldValue('clinicalId', '');
                  }}
                />,
              ])}
              {/* 查詢（:194-198 btnQuery；input[type=button] vs button＝GNL-004 同族形態差，
                  裁決 3 不 hack；complexBodySubmitId 自報＝C5-h 送出）。 */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  uiId={`${pageId}-search-submit`}
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={loading}
                  dataEffect={`list:${pageId}`}
                >
                  查詢
                </Button>
              </div>
              {/* csrfToken：Q2 裁決不畫（缺元素 1 筆既定殘留）。 */}
            </form>
          </div>
          {/* 右欄：importForm（planInstList.jsp:49-81 獨立 form；action="" 正規化成 /）。 */}
          <div>
            <form
              data-ui-id={`${pageId}-import-form`}
              action=""
              method="POST"
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <PlanInstImportToolbar
                pageId={pageId}
                jobTypeId={draftValues['jobTypeId'] ?? ''}
                uploadFileName={draftValues['uploadFile'] ?? ''}
                onUploadFileNameChange={(name) => setFieldValue('uploadFile', name)}
                onImported={reload}
              />
            </form>
          </div>
        </div>
        {/* hr-line-dashed（planInstList.jsp:87）：class-only div，兩側 normalize 皆剝除。 */}
        <div className="hr-line-dashed my-4 border-b border-border" />
        {/* 隱藏範本×4（templateForFiller.jsp:2-20；class="hidden" wrapper 兩側同被 R6 剝除，
            內容浮到頁面層＝legacy 正規化樹同形；§7-5 uiId 清單）。 */}
        <div className="hidden" data-ui-id={`${pageId}-tpl-filler`}>
          <select data-ui-id={`${pageId}-tpl-filler-dept`} aria-label="範本填寫者科部" />
          <select data-ui-id={`${pageId}-tpl-filler-select`} aria-label="範本填寫者" />
        </div>
        <div className="hidden" data-ui-id={`${pageId}-tpl-teacher-pool`}>
          <select data-ui-id={`${pageId}-tpl-teacher-pool-select`} aria-label="教師池" />
        </div>
        <div className="hidden" data-ui-id={`${pageId}-tpl-any`}>
          <input
            type="text"
            placeholder="(請輸入姓名或員編)"
            data-ui-id={`${pageId}-tpl-any-input`}
            aria-label="帳號搜尋"
          />
          <select data-ui-id={`${pageId}-tpl-any-select`} aria-label="帳號" />
        </div>
        {resultShell}
        {/* planInstDetailSample（planInstList.jsp:93-124 隱藏範本；兩列表頭逐字＋rowspan/
            colspan 鏡像——react 以 runtime rowExpanded 取代 legacy row.child 機制，初始態
            DOM 仍需這棵範本樹讓兩側 table 序列 1:1 對齊）。 */}
        <div className="hidden" data-ui-id={`${pageId}-detail-sample`}>
          <table data-ui-id={`${pageId}-detail-sample-table`} className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th rowSpan={2} className="w-[30px] p-2" />
                <th rowSpan={2} className="p-2 text-center">訓練期間</th>
                <th rowSpan={2} className="p-2 text-center">訓練階段</th>
                <th rowSpan={2} className="p-2 text-center">階段導師</th>
                <th rowSpan={2} className="p-2 text-center">臨床教師</th>
                <th rowSpan={2} className="p-2 text-center">訓練地點</th>
                <th colSpan={2} className="p-2 text-center">
                  <a className={`${LINK_TEXT_CLASS} cursor-pointer`}>表單</a>
                </th>
                <th colSpan={2} className="p-2 text-center">測驗</th>
              </tr>
              <tr>
                <th className="p-2 text-center">完成份數</th>
                <th className="p-2 text-center">完成度</th>
                <th className="p-2 text-center">完成數</th>
                <th className="p-2 text-center">完成度</th>
              </tr>
            </thead>
            <tbody />
          </table>
        </div>
      </ContentPanel>
      {/* 大 modal（showModalPlanInst planInstModalPlanInst.jsp；batch 15 B7-1 元件沿用；
          關閉態不渲染＝不進結構比對）。 */}
      {editing !== null ? (
        <PlanInstEditModal
          pageId={pageId}
          mode={editing.mode}
          row={editing.row}
          values={draftValues}
          onClose={() => setEditing(null)}
          onClosed={reload}
        />
      ) : null}
    </div>
  );
}
